<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div
      :disabled="disabled"
      :class="$style.container"
      :style="style.container"
    >
      <div
        :disabled="disabled"
        :class="$style.filler"
        :style="style.fillerLeft"
      >
        <slot
          name="left"
          :disabled="disabled"
          :tabIndex="tabIndex"
        />
      </div>
      <template v-for="(tab, index) in tabs">
        <div
          :key="index"
          :disabled="tab.disabled ? tab.disabled : false"
          :selected="tabIndex === index"
          @click="selectTab(tab, index)"
          :class="$style.tab"
          :style="style.tab">
          <slot
            name="tab"
            :disabled="tab.disabled ? tab.disabled : false"
            :index="index"
            :label="labelKey ? tab[labelKey] : tab"
            :selected="tabIndex === index"
            :tab="tab"
          />
        </div>
        <div
          v-if="index < tabs.length - 1"
          :key="`${index}_filler`"
          :disabled="disabled"
          :class="$style.filler"
          :style="style.filler"
        >
          <slot
            name="filler"
            :disabled="disabled"
            :index="index"
            :tabIndex="tabIndex"
          />
        </div>
      </template>
      <div
        :disabled="disabled"
        :class="$style.filler"
        :style="style.fillerRight"
      >
        <slot
          name="right"
          :disabled="disabled"
          :tabIndex="tabIndex"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { CssStyleProcessor } from '@js/vue-style-utils'

  export default {
    name: 'BaseTab',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change',
    },
    props: {
      vmodelValue: {
        type: Number,
        required: false,
        default: 0
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      labelKey: {
        type: [Object, String],
        required: false,
        default: 'label',
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {}
      },
      tabs: {
        type: Array,
        required: false,
        default: () => []
      },
    },
    data: function () {
      return {
        style: {
          container: {
            alignItems: 'flex-end',
            display: 'flex',
          },
          fillerLeft: {
            height: '100%',
          },
          fillerRight: {
            height: '100%',
            flex: 1,
          },
          filler: {
            height: '100%',
          },
          root: {},
          tab: {
            height: '100%',
          },
        },
        tabIndex: this.vmodelValue,
      };
    },
    created: function () {
      const proc = new CssStyleProcessor('--v2-base-tab', ['selected']);

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.fillerLeft,
        proc.getExtendedStyle(this.styleConfig.fillerLeft, 'filler'),
      );
      Object.assign(
        this.style.fillerRight,
        proc.getExtendedStyle(this.styleConfig.fillerRight, 'filler'),
      );
      Object.assign(
        this.style.filler,
        proc.getExtendedStyle(this.styleConfig.filler, 'filler'),
      );
      Object.assign(
        this.style.tab,
        proc.getExtendedStyle(this.styleConfig.tab, 'tab'),
      );

      Object.assign(
        this.style.root,
        this.styleConfig.root,
      );
    },
    methods: {
      selectTab: function (tab, index) {
        if (tab.disabled) {
          return;
        }
        this.tabIndex = index;
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.tabIndex = this.vmodelValue;
      },
      tabIndex: function (newVal, oldVal) {
        this.$emit('change', newVal);
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
    background-color var(--v2-base-tab-container-background-color)
    border var(--v2-base-tab-container-border)
    border-radius var(--v2-base-tab-container-border-radius)
    border-width var(--v2-base-tab-container-border-width)
    box-shadow var(--v2-base-tab-container-box-shadow)
    &:hover
      background-color var(--v2-base-tab-container-background-color-hover)
      border var(--v2-base-tab-container-border-hover)
      border-radius var(--v2-base-tab-container-border-radius-hover)
      border-width var(--v2-base-tab-container-border-width-hover)
      box-shadow var(--v2-base-tab-container-box-shadow-hover)
    &:active
      background-color var(--v2-base-tab-container-background-color-active)
      border var(--v2-base-tab-container-border-active)
      border-radius var(--v2-base-tab-container-border-radius-active)
      border-width var(--v2-base-tab-container-border-width-active)
      box-shadow var(--v2-base-tab-container-box-shadow-active)
    &[disabled]
      background-color var(--v2-base-tab-container-background-color-disabled)
      border var(--v2-base-tab-container-border-disabled)
      border-radius var(--v2-base-tab-container-border-radius-disabled)
      border-width var(--v2-base-tab-container-border-width-disabled)
      box-shadow var(--v2-base-tab-container-box-shadow-disabled)

  .filler
    background-color var(--v2-base-tab-filler-background-color)
    border var(--v2-base-tab-filler-border)
    border-radius var(--v2-base-tab-filler-border-radius)
    border-width var(--v2-base-tab-filler-border-width)
    box-shadow var(--v2-base-tab-filler-box-shadow)
    &:hover
      background-color var(--v2-base-tab-filler-background-color-hover)
      border var(--v2-base-tab-filler-border-hover)
      border-radius var(--v2-base-tab-filler-border-radius-hover)
      border-width var(--v2-base-tab-filler-border-width-hover)
      box-shadow var(--v2-base-tab-filler-box-shadow-hover)
    &:active
      background-color var(--v2-base-tab-filler-background-color-active)
      border var(--v2-base-tab-filler-border-active)
      border-radius var(--v2-base-tab-filler-border-radius-active)
      border-width var(--v2-base-tab-filler-border-width-active)
      box-shadow var(--v2-base-tab-filler-box-shadow-active)
    &[disabled]
      background-color var(--v2-base-tab-filler-background-color-disabled)
      border var(--v2-base-tab-filler-border-disabled)
      border-radius var(--v2-base-tab-filler-border-radius-disabled)
      border-width var(--v2-base-tab-filler-border-width-disabled)
      box-shadow var(--v2-base-tab-filler-box-shadow-disabled)

  .tab
    background-color var(--v2-base-tab-tab-background-color)
    border var(--v2-base-tab-tab-border)
    border-radius var(--v2-base-tab-tab-border-radius)
    border-width var(--v2-base-tab-tab-border-width)
    box-shadow var(--v2-base-tab-tab-box-shadow)
    color var(--v2-base-tab-tab-color)
    cursor var(--v2-base-tab-tab-cursor)
    font-family var(--v2-base-tab-tab-font-family)
    font-size var(--v2-base-tab-tab-font-size)
    font-weight var(--v2-base-tab-tab-font-weight)
    &[selected], &[selected]:hover
      background-color var(--v2-base-tab-tab-background-color-selected)
      border var(--v2-base-tab-tab-border-selected)
      border-radius var(--v2-base-tab-tab-border-radius-selected)
      border-width var(--v2-base-tab-tab-border-width-selected)
      box-shadow var(--v2-base-tab-tab-box-shadow-selected)
      color var(--v2-base-tab-tab-color-selected)
      cursor var(--v2-base-tab-tab-cursor-selected)
      font-family var(--v2-base-tab-tab-font-family-selected)
      font-size var(--v2-base-tab-tab-font-size-selected)
      font-weight var(--v2-base-tab-tab-font-weight-selected)
    &:hover
      background-color var(--v2-base-tab-tab-background-color-hover)
      border var(--v2-base-tab-tab-border-hover)
      border-radius var(--v2-base-tab-tab-border-radius-hover)
      border-width var(--v2-base-tab-tab-border-width-hover)
      box-shadow var(--v2-base-tab-tab-box-shadow-hover)
      color var(--v2-base-tab-tab-color-hover)
      cursor var(--v2-base-tab-tab-cursor-hover)
      font-family var(--v2-base-tab-tab-font-family-hover)
      font-size var(--v2-base-tab-tab-font-size-hover)
      font-weight var(--v2-base-tab-tab-font-weight-hover)
    &:active
      background-color var(--v2-base-tab-tab-background-color-active)
      border var(--v2-base-tab-tab-border-active)
      border-radius var(--v2-base-tab-tab-border-radius-active)
      border-width var(--v2-base-tab-tab-border-width-active)
      box-shadow var(--v2-base-tab-tab-box-shadow-active)
      color var(--v2-base-tab-tab-color-active)
      cursor var(--v2-base-tab-tab-cursor-active)
      font-family var(--v2-base-tab-tab-font-family-active)
      font-size var(--v2-base-tab-tab-font-size-active)
      font-weight var(--v2-base-tab-tab-font-weight-active)
    &[disabled]
      background-color var(--v2-base-tab-tab-background-color-disabled)
      border var(--v2-base-tab-tab-border-disabled)
      border-radius var(--v2-base-tab-tab-border-radius-disabled)
      border-width var(--v2-base-tab-tab-border-width-disabled)
      box-shadow var(--v2-base-tab-tab-box-shadow-disabled)
      color var(--v2-base-tab-tab-color-disabled)
      cursor var(--v2-base-tab-tab-cursor-disabled)
      font-family var(--v2-base-tab-tab-font-family-disabled)
      font-size var(--v2-base-tab-tab-font-size-disabled)
      font-weight var(--v2-base-tab-tab-font-weight-disabled)
</style>
